// ─────────────────────────────────────────────────────────────────────────────
// SurvAIHealth Metrics — navigation tree config
//
// A RECURSIVE, MIXED tree (not a flat list). Each node is EITHER:
//   • a group  → has `children: [...]`  (renders as a collapsible folder)
//   • a leaf   → has `reportKey`        (renders as a selectable report)
// A node has one or the other, never both.
//
// Grievance Log is flat today, but the Risk suite (next phase) will mix
// subgroups (Falls, Wounds) with loose reports at the same level. The tree and
// MetricsTreeNav are built to handle that now so Risk drops in with zero
// refactor — see the commented PHASE 2 template at the bottom.
// ─────────────────────────────────────────────────────────────────────────────

export const metricsNav = [
  {
    id: "grievance-log",
    label: "Grievance Log",
    children: [
      { id: "grv-dashboard", label: "Grievance Log Dashboard", reportKey: "GrievanceDashboard" },
      { id: "grv-list", label: "Grievance List", reportKey: "GrievanceList" },
      { id: "grv-overview", label: "Grievance Overview", reportKey: "GrievanceOverview" },
      { id: "grv-summaries", label: "Grievance Summaries", reportKey: "GrievanceSummaries" },
      { id: "grv-days", label: "Grievances by Days to Close", reportKey: "GrievancesByDaysToClose" },
      { id: "grv-keyword", label: "Grievances Keyword Search", reportKey: "GrievanceKeywordSearch" },
      { id: "grv-open-cat", label: "Open Grievances by Category", reportKey: "OpenByCategory" },
      { id: "grv-open-dept", label: "Open Grievances by Department", reportKey: "OpenByDepartment" },
      { id: "grv-employees", label: "Employees Mentioned", reportKey: "EmployeesMentioned" },
    ],
  },
  {
    id: "covid-19",
    label: "COVID-19",
    children: [
      { id: "covid-vax", label: "Vaccination Status", reportKey: "CovidVaccination" },
      { id: "covid-cases", label: "Case Log", reportKey: "CovidCaseLog" },
      { id: "covid-testing", label: "Testing Summary", reportKey: "CovidTesting" },
    ],
  },
  {
    id: "quality-measures",
    label: "Quality Measures",
    children: [
      { id: "qm-long", label: "Long-Stay Measures", reportKey: "QMLongStay" },
      { id: "qm-short", label: "Short-Stay Measures", reportKey: "QMShortStay" },
      { id: "qm-compare", label: "QM Facility Comparison", reportKey: "QMComparison" },
    ],
  },
  {
    id: "clinical",
    label: "Clinical",
    children: [
      { id: "clin-adc", label: "Advance Directive Compliance", reportKey: "AdvanceDirectiveCompliance" },
      { id: "clin-ama", label: "AMA Discharges", reportKey: "AMADischarges" },
      { id: "clin-arr", label: "Assessment Response Review", reportKey: "AssessmentResponseReview" },
      { id: "clin-assdue", label: "Assessments Due", reportKey: "AssessmentsDue" },
      { id: "clin-beds", label: "Available Beds", reportKey: "AvailableBeds" },
      { id: "clin-avgmeds", label: "Average Medications per Resident", reportKey: "AvgMedications" },
      { id: "clin-report", label: "Clinical Report", reportKey: "ClinicalReport" },
      { id: "clin-report-alil", label: "Clinical Report - AL and IL", reportKey: "ClinicalReportALIL" },
      { id: "clin-report-detail", label: "Clinical Report Detail", reportKey: "ClinicalReportDetail" },
      { id: "clin-facassess", label: "Data for Facility Assessment", reportKey: "FacilityAssessmentData" },
      { id: "clin-elope", label: "Elopement Audit", reportKey: "ElopementAudit" },
      { id: "clin-eod", label: "End Of Day", reportKey: "EndOfDay" },
      { id: "clin-adl", label: "ADL / Functional Documentation", reportKey: "AdlDocumentation" },
      { id: "clin-dx", label: "Resident Diagnoses (Problem List)", reportKey: "ResidentDiagnoses" },
      { id: "clin-episodes", label: "Episodes of Care (Stays)", reportKey: "EpisodesOfCare" },
      { id: "clin-gg", label: "GG Function Outcomes", reportKey: "GGFunctionOutcomes" },
      { id: "clin-hipps", label: "PDPM Case-Mix (HIPPS)", reportKey: "HippsPdpm" },
      { id: "clin-falls", label: "High Risk Falls", reportKey: "HighRiskFalls" },
      { id: "clin-htl", label: "Hospital Transfer Log", reportKey: "HospitalTransferLog" },
      { id: "clin-loct", label: "Level of Care Trend - AL and IL", reportKey: "LevelOfCareTrend" },
      { id: "clin-medsched", label: "Medication Schedules Listing", reportKey: "MedicationSchedules" },
      { id: "clin-newadmit", label: "New Admit Audit", reportKey: "NewAdmitAudit" },
      { id: "clin-packetcomp", label: "Packet Completion Summary", reportKey: "PacketCompletion" },
      { id: "clin-packetsign", label: "Packet Signer Detail", reportKey: "PacketSignerDetail" },
      { id: "clin-pn", label: "Progress Notes", reportKey: "ProgressNotes" },
      { id: "clin-pn1", label: "Progress Notes 1 on 1", reportKey: "ProgressNotes1on1" },
      { id: "clin-mds-recent", label: "Recently Completed MDS Assessments", reportKey: "RecentMDS" },
      { id: "clin-rehosp", label: "Rehospitalization Risk", reportKey: "RehospitalizationRisk" },
      { id: "clin-rehosprate", label: "Rehospitalization Rate (30-Day)", reportKey: "RehospitalizationRate" },
      { id: "clin-latest", label: "Resident Latest Assessment", reportKey: "ResidentLatestAssessment" },
      { id: "clin-uda", label: "UDA Schedule Audit", reportKey: "UDAScheduleAudit" },
      { id: "clin-uhd", label: "Unplanned Hospital Discharge", reportKey: "UnplannedHospitalDischarge" },
      { id: "clin-uhd-detail", label: "Unplanned Hospital Discharge Detail", reportKey: "UnplannedHospitalDischargeDetail" },
      { id: "clin-wtloss", label: "Weight Loss & Nutrition", reportKey: "WeightLossNutrition" },
    ],
  },
  {
    id: "risk",
    label: "Risk",
    children: [
      {
        id: "risk-falls",
        label: "Falls",
        children: [
          { id: "risk-falls-analysis", label: "Fall Analysis", reportKey: "FallAnalysis" },
          { id: "risk-falls-list", label: "Fall Incident List", reportKey: "FallIncidentList" },
          { id: "risk-falls-dash", label: "Falls Dashboard", reportKey: "FallsDashboard" },
        ],
      },
      {
        id: "risk-wounds",
        label: "Wounds",
        children: [
          { id: "risk-wounds-analysis", label: "Wound Analysis", reportKey: "WoundAnalysis" },
          { id: "risk-wounds-list", label: "Wound Incident List", reportKey: "WoundIncidentList" },
        ],
      },
      { id: "risk-overview", label: "All Facilities Overview", reportKey: "IncidentAllFacilities" },
      { id: "risk-dup", label: "Duplicate Incident", reportKey: "DuplicateIncident" },
      { id: "risk-faccompare", label: "Facility Comparison", reportKey: "IncidentFacilityComparison" },
      { id: "risk-cpaudit", label: "Incident Care Plan Audit", reportKey: "IncidentCarePlanAudit" },
      { id: "risk-detail", label: "Incident Detail", reportKey: "IncidentDetail" },
      { id: "risk-list", label: "Incident List", reportKey: "IncidentList" },
      { id: "risk-trend", label: "Incident Trend", reportKey: "IncidentTrend" },
      { id: "risk-injury", label: "Injury Detail", reportKey: "InjuryDetail" },
      { id: "risk-repeat", label: "Resident Repeat Incident", reportKey: "ResidentRepeatIncident" },
      { id: "risk-response", label: "Response Time Summary", reportKey: "ResponseTimeSummary" },
    ],
  },
  {
    id: "sys",
    label: "Sys",
    children: [
      { id: "sys-available", label: "Available Reports", reportKey: "AvailableReports" },
      { id: "sys-software", label: "Client Software Mapping", reportKey: "ClientSoftwareMapping" },
      { id: "sys-subs", label: "Client Subscription List", reportKey: "ClientSubscriptionList" },
      { id: "sys-jobs", label: "Jobs Run History", reportKey: "JobsRunHistory" },
      { id: "sys-census-load", label: "PointClickCare Daily Census Load Log", reportKey: "CensusLoadLog" },
      { id: "sys-roleperm", label: "Role Permission", reportKey: "RolePermission" },
    ],
  },
  {
    id: "admin",
    label: "Admin",
    children: [
      { id: "adm-acct-audit", label: "User Account Audit", reportKey: "UserAccountAudit" },
      { id: "adm-acct-change", label: "User Account Change Log", reportKey: "UserAccountChangeLog" },
      { id: "adm-usage-detail", label: "Report Usage Detail", reportKey: "ReportUsageDetail" },
      { id: "adm-usage-summary", label: "Report Usage Summary by User", reportKey: "ReportUsageSummary" },
      { id: "adm-userlist", label: "User List", reportKey: "UserList" },
    ],
  },
];

// Returns true when a node is a leaf (selectable report).
export const isLeaf = (node) => node && !!node.reportKey && !node.children;

// Returns true when a node is a group (collapsible folder).
export const isGroup = (node) => node && Array.isArray(node.children);

// Depth-first: the first leaf in the tree. Used to seed the default selection.
export function findFirstLeaf(nodes) {
  for (const node of nodes || []) {
    if (isLeaf(node)) return node;
    if (isGroup(node)) {
      const found = findFirstLeaf(node.children);
      if (found) return found;
    }
  }
  return null;
}

// The id of the first top-level group — seeds the default expanded state.
export function firstGroupId(nodes) {
  const g = (nodes || []).find((n) => isGroup(n));
  return g ? g.id : null;
}

// Flatten every leaf into [{ id, label, reportKey }] — powers the mobile dropdown.
export function flattenLeaves(nodes, trail = []) {
  let out = [];
  for (const node of nodes || []) {
    if (isLeaf(node)) {
      out.push({ id: node.id, label: node.label, reportKey: node.reportKey, trail });
    } else if (isGroup(node)) {
      out = out.concat(flattenLeaves(node.children, [...trail, node.label]));
    }
  }
  return out;
}
